import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { toFullName } from '@repo/toolkit/to-full-name'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getHostFamilies } from '../../../../api/host-families/get-host-families'
import useTranslate from '../../../../lib/hooks/use-translate'
import { ChangeHostFamilyInput } from '../../../../lib/types/models/center/assign-host-family'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedOption } from '../../../../lib/utils/functions/misc.functions'
import { createNumberSchema } from '../../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import Form, { FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'

type Props = BaseFormProps<ChangeHostFamilyInput> & {
  olId: number
  className?: string
}

const formSchema = (t: TFunction<AvailableLanguages, undefined>): ZodType<ChangeHostFamilyInput> =>
  z.object({
    hostFamilyId: createNumberSchema('Host family', { nullish: true }),
    reasonForChange: createStringSchema('Reason for change', {
      errorMessage: t('ol.center.host.family.reason.for.change.required')
    })
  })

const OlCenterChangeHostFamilyForm = ({
  defaultValues,
  className,
  olId,
  ...validationButtonProps
}: Props) => {
  const { t } = useTranslate()
  const form = useForm<ChangeHostFamilyInput>({
    resolver: zodResolver(formSchema(t)),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: hostFamilies } = useQuery({
    queryKey: ['host-families', olId],
    queryFn: () =>
      getHostFamilies({
        filter: { olId, deactivated: false }
      })
  })

  return (
    <Form form={form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className="divide-y divide-white-active">
          <div className={className}>
            <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
              Please choose a host family from the list below :
            </Typography>
            <div className="h-2" />
            <FormField
              control={form.control}
              name={'hostFamilyId'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      optional
                      optionalText="None"
                      label={'Host family'}
                      searchable
                      options={
                        hostFamilies?.data.map((hostFamily) => ({
                          label: toFullName(
                            hostFamily.parent1FirstName,
                            hostFamily.parent1LastName,
                            { options: { mode: 'lastname-firstname' } }
                          ),
                          value: hostFamily.id
                        })) ?? []
                      }
                      selectedOption={getSelectedOption(
                        field.value,
                        toFullName(
                          hostFamilies?.data.find((hostFamily) => hostFamily.id === field.value)
                            ?.parent1FirstName,
                          hostFamilies?.data.find((hostFamily) => hostFamily.id === field.value)
                            ?.parent1LastName,
                          { options: { mode: 'lastname-firstname' } }
                        )
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="h-3" />
            <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
              Reason for Change*
            </Typography>

            <div className="h-2" />
            <FormField
              control={form.control}
              name={'reasonForChange'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput {...field} type="textarea" placeholder="Write here" />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          <div className="pb-6 px-4">
            <ValidationButtons {...validationButtonProps} />
          </div>
        </div>
      </form>
    </Form>
  )
}

export default OlCenterChangeHostFamilyForm
